import clsx from 'clsx';
import { Trans, useTranslation } from 'next-i18next/pages';
import dynamic from 'next/dynamic';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import { JobPosition } from '@pflegia/types/api/job-position/job-position';
import { ConnectionInfo } from '@pflegia/types/common/match/dialog';
import { CardStatus, CardType } from '@pflegia/ui/pflegia/types/card';
import { getButtonClassName } from '@pflegia/ui/shared/button';
import LinkWithLoader from '@pflegia/ui/shared/link-with-loader/LinkWithLoader.component';
import { mergeTailwindClasses } from '@pflegia/util/functions/style/merge-tailwind-classes';

import { OffersDialogueButtonSkeleton } from '@components/skeleton/OffersDialogueButtonSkeleton';

import { CHAT_BY_JOB_POSITION_ID } from '@shared/application/constants';
import { useRouterHelper } from '@shared/hooks/use-router-helper';

const OffersDialogueButton = dynamic(() => import('@components/dialogue'), {
  ssr: false,
  loading: () => <OffersDialogueButtonSkeleton />,
});

export interface StickyBottomSectionProps {
  className?: string;
  openDialog: (connectionInfo: ConnectionInfo) => void;
  jobPosition: JobPosition;
  invitationType: CardType;
  status: CardStatus;
  applyLabel?: string;
}

const StickyBottomSection: React.FC<StickyBottomSectionProps> = ({
  openDialog,
  jobPosition,
  invitationType,
  status,
  applyLabel,
  className,
}) => {
  const { t } = useTranslation('jobDetails');
  const { isTransparentPath, isExternalJob } = useRouterHelper();
  const buttonClassName = 'w-auto py-4 mx-6';

  return (
    <div className='z-sticky sticky right-0 bottom-0 left-0'>
      <div className={twMerge(clsx('shadow-upper flex flex-col bg-white transition-all lg:z-50', className))}>
        {invitationType === 'application' && status === 'pending' && (
          <span className='rounded-none pt-4 pb-8 text-center text-sm'>
            <Trans t={t} i18nKey='applicationHeader.application.pending.mobile' components={{ span: <React.Fragment /> }} />
          </span>
        )}
        {invitationType === 'invitation' && status === 'pending' && (
          <>
            <OffersDialogueButton
              className={mergeTailwindClasses(buttonClassName, 'mt-4 md:my-6')}
              jobPositionId={jobPosition.id}
              label={t('applicationHeader.accept')}
              status='pending'
              jobTitle={jobPosition.title}
              type='invitation'
              employerName={jobPosition.employer.name}
              openDialog={openDialog}
              slug={jobPosition.slug}
            />
            <OffersDialogueButton
              className={clsx('text-error border-none bg-transparent', buttonClassName)}
              jobPositionId={jobPosition.id}
              label={t('applicationHeader.decline')}
              status='declined'
              jobTitle={jobPosition.title}
              employerName={jobPosition.employer.name}
              openDialog={openDialog}
              slug={jobPosition.slug}
            />
          </>
        )}
        {invitationType === 'default' && (
          <>
            {isTransparentPath ? (
              <OffersDialogueButton
                className={buttonClassName}
                jobPositionId={jobPosition.id || ''}
                label={applyLabel ?? t(isExternalJob ? 'contactPerson.apply' : 'contactPerson.stickyApplyButton')}
                jobTitle={jobPosition.title}
                employerName={jobPosition.employer.name}
                openDialog={openDialog}
                colorScheme='primary'
                slug={jobPosition.slug}
              />
            ) : (
              <OffersDialogueButton
                jobPositionId={jobPosition.id}
                status={status}
                jobTitle={jobPosition.title}
                label={t('contactPerson.apply')}
                className={buttonClassName}
                employerName={jobPosition.employer.name}
                openDialog={openDialog}
                slug={jobPosition.slug}
              />
            )}
          </>
        )}
        {status === 'accepted' && (
          <LinkWithLoader href={CHAT_BY_JOB_POSITION_ID(jobPosition.id)} className={getButtonClassName('apply', buttonClassName)}>
            {t('applicationHeader.inbox')}
          </LinkWithLoader>
        )}
      </div>
    </div>
  );
};

export default StickyBottomSection;
